import {
  getAccountState,
  getActiveAssetData,
  getAssetContext,
  getAssetMeta,
  getCandleData,
  getMarkets,
  getMidPrices,
  getOpenOrders,
  getPortfolio,
  getTradingBalance,
  getUserAbstraction,
  getUserFills,
  type HLCandle,
} from "@src/features/trade/hyperliquid/services/hyperliquidService"
import type {
  HLAccountAbstraction,
  HLDexName,
  HLFill,
  HLMarketInfo,
  HLTradingBalance,
  Result,
} from "@src/features/trade/hyperliquid/types"
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useMemo } from "react"
import type { Address } from "viem"
import { MAIN_DEX } from "../marketIdentity"
import { hyperliquidQueryKeys } from "../queries"

export const HYPERLIQUID_SPARKLINE_CANDLES_STALE_TIME = 10 * 60_000
export const HYPERLIQUID_USER_ABSTRACTION_STALE_TIME = 30 * 60_000

type HyperliquidCandleInterval =
  | "1m"
  | "3m"
  | "5m"
  | "15m"
  | "1h"
  | "4h"
  | "8h"
  | "1d"
  | "1w"

function successfulResultStaleTime(staleTime: number) {
  return (query: { state: { data: unknown } }) => {
    return isSuccessfulResult(query.state.data) ? staleTime : 0
  }
}

function isSuccessfulResult(data: unknown): data is { ok: true } {
  return (
    typeof data === "object" && data != null && "ok" in data && data.ok === true
  )
}

export function useHyperliquidMarkets() {
  return useQuery<Result<HLMarketInfo[]>>({
    queryKey: hyperliquidQueryKeys.markets(),
    queryFn: async ({ signal }) => {
      const result = await getMarkets(signal)
      if (!result.ok) throw new Error(result.error)
      return result
    },
    staleTime: 60_000,
    retry: 2,
  })
}

export function useHyperliquidMidPrices(
  options: { coin?: string; live?: boolean } = {}
) {
  const selectMidPrice = useMemo(
    () => (result: Result<Record<string, string>>) => {
      if (!result.ok || !options.coin) return result
      const price = result.data[options.coin]
      return {
        ok: true as const,
        data: price == null ? {} : { [options.coin]: price },
      }
    },
    [options.coin]
  )

  return useQuery({
    queryKey: hyperliquidQueryKeys.mids(),
    queryFn: ({ signal }) => getMidPrices(signal),
    select: selectMidPrice,
    refetchInterval: options.live ? false : 5_000,
    staleTime: 2_000,
  })
}

export function useHyperliquidAccount(
  address?: Address,
  dex: HLDexName = MAIN_DEX,
  options: { live?: boolean } = {}
) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.account(address, dex),
    queryFn: ({ signal }) => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      return getAccountState(address, { dex, signal })
    },
    enabled: !!address,
    refetchInterval: options.live ? false : 10_000,
    staleTime: 5_000,
  })
}

export function useHyperliquidUserAbstraction(address?: Address) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.userAbstraction(address),
    queryFn: ({ signal }) => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      return getUserAbstraction(address, signal)
    },
    enabled: !!address,
    staleTime: successfulResultStaleTime(
      HYPERLIQUID_USER_ABSTRACTION_STALE_TIME
    ),
  })
}

export function useHyperliquidTradingBalance(
  address?: Address,
  dex: HLDexName = MAIN_DEX
) {
  const queryClient = useQueryClient()

  return useQuery<Result<HLTradingBalance>>({
    queryKey: hyperliquidQueryKeys.tradingBalance(address, dex),
    queryFn: async ({ signal }): Promise<Result<HLTradingBalance>> => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      const abstraction = await queryClient.fetchQuery<
        Result<HLAccountAbstraction>
      >({
        queryKey: hyperliquidQueryKeys.userAbstraction(address),
        queryFn: ({ signal: abstractionSignal }) =>
          getUserAbstraction(address, abstractionSignal),
        staleTime: successfulResultStaleTime(
          HYPERLIQUID_USER_ABSTRACTION_STALE_TIME
        ),
      })
      if (!abstraction.ok) {
        return { ok: false, error: abstraction.error }
      }
      return getTradingBalance(address, {
        dex,
        signal,
        knownAbstraction: abstraction.data,
      })
    },
    enabled: !!address,
    refetchInterval: 10_000,
    staleTime: 5_000,
  })
}

export function useHyperliquidPortfolio(address?: Address) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.portfolio(address),
    queryFn: ({ signal }) => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      return getPortfolio(address, signal)
    },
    enabled: !!address,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

export function useHyperliquidAssetMeta(coin: string) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.assetMeta(coin),
    queryFn: ({ signal }) => getAssetMeta(coin, signal),
    enabled: !!coin,
    staleTime: 300_000,
  })
}

export function useHyperliquidAssetContext(coin: string) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.assetContext(coin),
    queryFn: ({ signal }) => getAssetContext(coin, signal),
    enabled: !!coin,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

export function useHyperliquidActiveAsset(address?: Address, coin?: string) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.activeAsset(address, coin),
    queryFn: ({ signal }) => {
      if (!address || !coin) {
        throw new Error("Wallet address and coin required")
      }
      return getActiveAssetData(address, coin, signal)
    },
    enabled: !!address && !!coin,
    staleTime: 30_000,
  })
}

export function useHyperliquidCandles(
  coin: string,
  interval: HyperliquidCandleInterval,
  lookbackMs: number,
  options: { enabled?: boolean; live?: boolean } = {}
) {
  type CandlePageParam = "latest" | { endTime: number }
  const isSparseInterval =
    interval === "8h" || interval === "1d" || interval === "1w"
  const shouldRefetchHistory = (activeQuery: {
    state: { data?: InfiniteData<HLCandle[]> }
  }) => isSparseInterval || (activeQuery.state.data?.pages.length ?? 0) <= 1

  const query = useInfiniteQuery<
    HLCandle[],
    Error,
    InfiniteData<HLCandle[], CandlePageParam>,
    ReturnType<typeof hyperliquidQueryKeys.candles>,
    CandlePageParam
  >({
    queryKey: hyperliquidQueryKeys.candles(coin, interval, lookbackMs),
    queryFn: ({ pageParam, signal }) =>
      fetchCandlePage(
        coin,
        interval,
        lookbackMs,
        signal,
        pageParam === "latest" ? Date.now() : pageParam.endTime
      ),
    initialPageParam: "latest" as const,
    getNextPageParam: (lastPage, pages) =>
      getOlderCandlePageParam(lastPage, pages.length),
    enabled: !!coin && options.enabled !== false,
    refetchInterval: options.live
      ? false
      : isSparseInterval
        ? 5 * 60_000
        : (activeQuery) =>
            (activeQuery.state.data?.pages.length ?? 0) > 1 ? false : 15_000,
    refetchOnMount: shouldRefetchHistory,
    refetchOnReconnect: shouldRefetchHistory,
    refetchOnWindowFocus: shouldRefetchHistory,
    staleTime: isSparseInterval ? 5 * 60_000 : 10_000,
    gcTime: 30 * 60_000,
    retry: 2,
  })

  const hasHistoricalPages = (query.data?.pages.length ?? 0) > 1
  const refreshLatestSeparately = !isSparseInterval && hasHistoricalPages
  const latestPageQuery = useQuery<HLCandle[], Error>({
    queryKey: [
      ...hyperliquidQueryKeys.candles(coin, interval, lookbackMs),
      "latest-refresh",
    ],
    queryFn: ({ signal }) =>
      fetchCandlePage(coin, interval, lookbackMs, signal, Date.now()),
    enabled:
      !!coin && options.enabled !== false && refreshLatestSeparately,
    refetchInterval: options.live ? false : 15_000,
    staleTime: 10_000,
    retry: 2,
  })

  const data = useMemo(() => {
    const pages = query.data?.pages
    return mergeCandlePages(
      latestPageQuery.data ? [latestPageQuery.data, ...(pages ?? [])] : pages
    )
  }, [latestPageQuery.data, query.data])

  return {
    ...query,
    data,
    refetch: refreshLatestSeparately ? latestPageQuery.refetch : query.refetch,
  }
}

export const MAX_CANDLE_HISTORY_PAGES = 24

export function getOlderCandlePageParam(
  lastPage: HLCandle[],
  pageCount: number
) {
  if (pageCount >= MAX_CANDLE_HISTORY_PAGES) return undefined
  const oldestCandle = lastPage[0]
  if (!oldestCandle) return undefined
  return { endTime: oldestCandle.time - 1 }
}

async function fetchCandlePage(
  coin: string,
  interval: HyperliquidCandleInterval,
  lookbackMs: number,
  signal: AbortSignal,
  endTime: number
) {
  const result = await getCandleData(
    coin,
    interval,
    lookbackMs,
    signal,
    endTime
  )
  if (!result.ok) throw new Error(result.error)
  return result.data
}

function mergeCandlePages(
  pages: HLCandle[][] = []
): { ok: true; data: HLCandle[] } | undefined {
  if (pages.length === 0) return undefined

  const candlesByTime = new Map<number, HLCandle>()
  for (const page of pages.toReversed()) {
    for (const candle of page) {
      candlesByTime.set(candle.time, candle)
    }
  }

  return {
    ok: true,
    data: Array.from(candlesByTime.values()).sort((a, b) => a.time - b.time),
  }
}

export function useHyperliquidSparklineCandles(
  coin: string,
  lookbackMs = 24 * 60 * 60 * 1000
) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.sparklineCandles(coin, lookbackMs),
    queryFn: ({ signal }) => getCandleData(coin, "1h", lookbackMs, signal),
    enabled: !!coin,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: HYPERLIQUID_SPARKLINE_CANDLES_STALE_TIME,
  })
}

export function useMiniCandles(coin: string) {
  return useHyperliquidSparklineCandles(coin, 12 * 60 * 60 * 1000)
}

export function useHyperliquidFills(
  address?: Address,
  coin?: string,
  limit = 50
) {
  const selectFills = useMemo(
    () => (result: Result<HLFill[]>) => {
      if (!result.ok) return result

      const fills = coin
        ? result.data.filter((fill) => fill.coin === coin)
        : result.data
      return {
        ok: true as const,
        data: limit ? fills.slice(0, limit) : fills,
      }
    },
    [coin, limit]
  )

  return useQuery({
    queryKey: hyperliquidQueryKeys.fills(address),
    queryFn: ({ signal }) => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      return getUserFills(address, {}, signal)
    },
    select: selectFills,
    enabled: !!address,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

export function useHyperliquidOpenOrders(
  address?: Address,
  dex: HLDexName = MAIN_DEX
) {
  return useQuery({
    queryKey: hyperliquidQueryKeys.openOrders(address, dex),
    queryFn: ({ signal }) => {
      if (!address) {
        throw new Error("Wallet address required")
      }
      return getOpenOrders(address, { dex, signal })
    },
    enabled: !!address,
    refetchInterval: 10_000,
    staleTime: 5_000,
  })
}
